import { choiceQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { toggleQuery } from "components/scrapEyeDataDashboard/navigation/queryCodecs";
import { useNavigationQuery } from "components/scrapEyeDataDashboard/navigation/NavigationQueryProvider";
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useMemo } from "react";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import { Layout, Shape } from "plotly.js";
import { useMaterialColor, useMaterialDisplayName } from "hooks/useMaterials";
import { HeatSeries } from "src/store/api/generatedApi";
import { ChunkData } from "components/scrapTracking/helpers";
import {
  CREW_COLORS_TRANSLUCENT,
  CREW_COLORS_SOLID,
} from "components/common/plots/colorSets";

interface Props {
  heatsData: { series: HeatSeries[] } | undefined;
  start: Date;
  end: Date;
  chunks: ChunkData<unknown>[];
}

type MovingAverageWindow = "none" | "1day" | "7days" | "14days";

const MOVING_AVERAGE_OPTIONS: {
  value: MovingAverageWindow;
  labelKey: string;
  windowMs: number | null;
}[] = [
  { value: "none", labelKey: "none", windowMs: null },
  { value: "1day", labelKey: "oneDay", windowMs: 24 * 60 * 60 * 1000 },
  { value: "7days", labelKey: "sevenDays", windowMs: 7 * 24 * 60 * 60 * 1000 },
  {
    value: "14days",
    labelKey: "fourteenDays",
    windowMs: 14 * 24 * 60 * 60 * 1000,
  },
];

export const MaterialUsagePlot = ({ heatsData, start, end, chunks }: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();
  const { getColor } = useMaterialColor();
  const materialDisplayName = useMaterialDisplayName();
  const [showCrewBackground, setShowCrewBackground] = useNavigationQuery(
    "analysisCrewBackground",
    false,
    toggleQuery,
    "replace"
  );
  const [movingAverageWindow, setMovingAverageWindow] = useNavigationQuery(
    "analysisAverage",
    "none",
    choiceQuery(["none", "1day", "7days", "14days"] as const),
    "replace"
  );

  const loadingShapes = useMemo(() => {
    return chunks
      .filter((chunk) => chunk.value.status !== "success")
      .map((chunk) => ({
        type: "rect" as const,
        xref: "x" as const,
        yref: "paper" as const,
        x0: new Date(chunk.start),
        x1: new Date(chunk.end),
        y0: 0,
        y1: 1,
        fillcolor: "grey",
        opacity: 0.15,
        layer: "below" as const,
        line: { width: 0 },
      }));
  }, [chunks]);

  // Extract unique material names from the heats data
  const materials = useMemo(() => {
    if (!heatsData?.series) return [];

    const materialSet = new Set<string>();
    heatsData.series.forEach((heat) => {
      heat.drops.forEach((drop) => {
        materialSet.add(drop.material_name);
      });
    });

    // Sort alphabetically for consistent ordering
    return Array.from(materialSet).sort();
  }, [heatsData]);

  const plotData = useMemo(() => {
    if (!heatsData?.series || materials.length === 0) {
      return [];
    }

    // Sort heats by start time
    const sortedHeats = [...heatsData.series].sort(
      (a, b) =>
        new Date(a.start_timestamp).getTime() -
        new Date(b.start_timestamp).getTime()
    );

    // Initialize material data with Date objects for timezone shifting
    const materialData: Record<string, { x: Date[]; y: number[] }> = {};
    materials.forEach((material) => {
      materialData[material] = { x: [], y: [] };
    });

    sortedHeats.forEach((heat) => {
      if (heat.drops.length === 0) return;

      const timestamp = new Date(heat.start_timestamp);

      // Count drops per material for this heat
      const dropCounts: Record<string, number> = {};
      materials.forEach((material) => {
        dropCounts[material] = 0;
      });

      heat.drops.forEach((drop) => {
        const materialName = drop.material_name;
        if (dropCounts[materialName] !== undefined) {
          dropCounts[materialName]++;
        }
      });

      // Add data points for materials with lifts in this heat
      materials.forEach((material) => {
        const count = dropCounts[material] ?? 0;
        const entry = materialData[material];
        if (count > 0 && entry) {
          entry.x.push(timestamp);
          entry.y.push(count);
        }
      });
    });

    // Compute moving averages if requested
    const movingAvgOption = MOVING_AVERAGE_OPTIONS.find(
      (opt) => opt.value === movingAverageWindow
    );
    const windowMs = movingAvgOption?.windowMs;

    // If no moving average, return base traces
    if (!windowMs) {
      return materials.map((material) => ({
        x: materialData[material]?.x ?? [],
        y: materialData[material]?.y ?? [],
        type: "scatter" as const,
        mode: "lines+markers" as const,
        name: materialDisplayName(material),
        line: {
          color: getColor(material),
          width: 2,
        },
        hovertemplate: `${materialDisplayName(material)}: %{y} grabs<extra></extra>`,
      }));
    }

    // Replace with moving average traces
    return materials
      .map((material) => {
        const data = materialData[material];
        if (!data || data.x.length < 2) return null;

        const maX: Date[] = [];
        const maY: number[] = [];

        // For each point, compute average of points within window before it
        data.x.forEach((currentTime, idx) => {
          const currentMs = currentTime.getTime();
          const windowStart = currentMs - windowMs;

          let sum = 0;
          let count = 0;

          // Look at all points in the window
          for (let i = 0; i <= idx; i++) {
            const pointX = data.x[i];
            const pointY = data.y[i];
            if (pointX === undefined || pointY === undefined) continue;
            const pointMs = pointX.getTime();
            if (pointMs >= windowStart && pointMs <= currentMs) {
              sum += pointY;
              count++;
            }
          }

          if (count > 0) {
            maX.push(currentTime);
            maY.push(sum / count);
          }
        });

        return {
          x: maX,
          y: maY,
          type: "scatter" as const,
          mode: "lines" as const,
          name: materialDisplayName(material),
          line: {
            color: getColor(material),
            width: 2,
          },
          hovertemplate: `${materialDisplayName(material)}: %{y:.1f} avg grabs<extra></extra>`,
        };
      })
      .filter((trace): trace is NonNullable<typeof trace> => trace !== null);
  }, [
    heatsData,
    materials,
    getColor,
    movingAverageWindow,
    materialDisplayName,
  ]);

  // Extract crew periods for background shading and unique crews
  const { crewShapes, uniqueCrews } = useMemo(() => {
    if (!showCrewBackground || !heatsData?.series) {
      return { crewShapes: [], uniqueCrews: [] };
    }

    const sortedHeats = [...heatsData.series].sort(
      (a, b) =>
        new Date(a.start_timestamp).getTime() -
        new Date(b.start_timestamp).getTime()
    );

    const shapes: Partial<Shape>[] = [];
    const crewSet = new Set<string>();

    sortedHeats.forEach((heat) => {
      const crewId = heat.crew_id;
      crewSet.add(crewId);
      const color = CREW_COLORS_TRANSLUCENT[crewId] ?? "#cccccc30";
      const startTime = new Date(heat.start_timestamp);
      const endTime = new Date(heat.tap_timestamp ?? heat.start_timestamp);

      shapes.push({
        type: "rect",
        xref: "x",
        yref: "paper",
        x0: startTime,
        x1: endTime,
        y0: 0,
        y1: 1,
        fillcolor: color,
        line: { width: 0 },
        layer: "below",
      });
    });

    return {
      crewShapes: shapes,
      uniqueCrews: Array.from(crewSet).sort(),
    };
  }, [showCrewBackground, heatsData]);

  const layout: Partial<Layout> = useMemo(
    () => ({
      xaxis: {
        range: [start, end],
        type: "date",
        showgrid: true,
        gridcolor: theme.palette.grey[100],
      },
      yaxis: {
        showgrid: true,
        gridcolor: theme.palette.grey[100],
        title: { text: t("grabs") },
        rangemode: "tozero",
      },
      margin: { t: 20, r: 20, l: 50, b: 40 },
      height: 300,
      showlegend: true,
      legend: { orientation: "h", y: 1.15 },
      hovermode: "x unified",
      dragmode: "pan",
      shapes: [...loadingShapes, ...crewShapes],
    }),
    [start, end, t, loadingShapes, crewShapes, theme.palette.grey]
  );

  return (
    <Card>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">{t("materialUsageOverTime")}</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>{t("movingAverage")}</InputLabel>
              <Select
                value={movingAverageWindow}
                label={t("movingAverage")}
                onChange={(e) => {
                  setMovingAverageWindow(e.target.value);
                }}
              >
                {MOVING_AVERAGE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              variant={showCrewBackground ? "contained" : "outlined"}
              onClick={() => {
                setShowCrewBackground(!showCrewBackground);
              }}
            >
              {t("showCrewBackground")}
            </Button>
          </Stack>
        </Stack>

        {/* Crew legend when background is shown */}
        {showCrewBackground && uniqueCrews.length > 0 ? (
          <Stack direction="row" spacing={2} sx={{ mb: 1 }} flexWrap="wrap">
            <Typography variant="caption" color="text.secondary">
              {t("crews")}:
            </Typography>
            {uniqueCrews
              .filter((crew) => crew.length === 1)
              .map((crew) => (
                <Stack
                  key={crew}
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                >
                  <Box
                    sx={{
                      width: 16,
                      height: 16,
                      bgcolor: CREW_COLORS_SOLID[crew] ?? "#cccccc",
                      borderRadius: 0.5,
                    }}
                  />
                  <Typography variant="caption">{crew}</Typography>
                </Stack>
              ))}
          </Stack>
        ) : null}

        <Box sx={{ height: 300 }}>
          <ScrapTrackingViewportPlot
            data={plotData}
            layout={layout}
            style={{ width: "100%", height: "100%" }}
            config={{
              displayModeBar: false,
              responsive: true,
              scrollZoom: false,
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );
};
